import React, {useState, useEffect} from "react";
import {
    Typography,
    Card,
    CardHeader,
    CardBody,
    Button,
    Textarea,
    Select,
    Option,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
} from "@material-tailwind/react";
import InputMask from "react-input-mask";
import axios from "axios";
import { XMarkIcon, TrashIcon } from "@heroicons/react/24/solid";

const RegisterUnofficialDiscount = ({ currentRole, currentUser, discount, editDisabled }) => {
    const [formData, setFormData] = useState({
        id: discount?.id || null,
        name: discount?.name || "",
        note: discount?.note || "",
        authorization_image: null,
        authorization_image_url: discount?.authorization_image_url || "",
        admin_approved: discount?.admin_approved || false,
        treasury_approved: discount?.treasury_approved || false,
        discount_percentage: discount?.discount_percentage || 0,
        parcel_code_list: discount?.parcel_code_list || [],
    });
    const [adminAproved, setAdminAproved] = useState(discount?.admin_approver?.username || "")
    const [treasuryAproved, setTreasuryAproved] = useState(discount?.treasury_approver?.username || "")
    const [imagePreview, setImagePreview] = useState("")
    const [showDialog, setShowDialog] = useState(false);
    const [reqError, setReqError] = useState(null);
    const [formErrors, setFormErrors] = useState([]);

    useEffect(() => {
        if (formData.authorization_image_url) {
            setImagePreview(formData.authorization_image_url);
        }
    }, [formData.authorization_image_url]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleParcelCodeChange = (index, value) => {
        const updatedList = [...formData.parcel_code_list];
        updatedList[index] = value;
        setFormData((prev) => ({ ...prev, parcel_code_list: updatedList }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prevData) => ({
                ...prevData,
                authorization_image: file,
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const addParcelCode = () => {
        setFormData((prev) => ({
            ...prev,
            parcel_code_list: [...prev.parcel_code_list, ""],
        }));
    };

    const removeParcelCode = (index) => {
        setFormData((prev) => ({
            ...prev,
            parcel_code_list: prev.parcel_code_list.filter((_, i) => i !== index),
        }));
    };

    const submitForm = async () => {
        const url = formData.id ? `/unofficial_discounts/${formData.id}` : `/unofficial_discounts`;
        const method = formData.id ? "put" : "post";

        const formDataToSend = new FormData();

        Object.keys(formData).forEach((key) => {
            if (formData[key] !== undefined && formData[key] !== null && key !== "authorization_image_url") {
                if (key === "parcel_code_list" && Array.isArray(formData[key])) {
                    formData[key].forEach((item) => {
                        formDataToSend.append(`unofficial_discount[parcel_code_list][]`, item.trim());
                    });
                } else {
                    formDataToSend.append(`unofficial_discount[${key}]`, formData[key]);
                }
            }
        });

        if (formData.authorization_image && formData.authorization_image instanceof File) {
            formDataToSend.append("authorization_image", formData.authorization_image);
        }

        try {
            await axios({
                method,
                url,
                data: formDataToSend,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            window.location.href = "/discounts";
        } catch (error) {
            setReqError(error.response?.data || "An error occurred");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = [];

        if (!formData.name.trim()) {
            newErrors.push("El nombre es obligatorio.");
        }

        if (!formData.note.trim()) {
            newErrors.push("La nota es obligatoria.");
        }

        if (!formData.authorization_image && !formData.authorization_image_url) {
            newErrors.push("La imagen de autorización es obligatoria.");
        }

        if (formData.parcel_code_list.length === 0) {
            newErrors.push("Debe agregar al menos un código de predio.");
        }

        if (newErrors.length > 0) {
            setFormErrors(newErrors);
            return;
        }

        if (formData.admin_approved && formData.treasury_approved) {
            setShowDialog(true);
            return;
        }

        setFormErrors([]);
        submitForm();
    };

    const handleDialogConfirm = () => {
        setShowDialog(false);
        submitForm();
    };

    const handleDialogCancel = () => {
        setShowDialog(false);
    };


    return (
        <>
            <div className="mt-12 flex justify-center">
                <Card className="w-full border shadow-sm rounded-lg">
                    <CardHeader
                        floated={false}
                        shadow={false}
                        color="transparent"
                        className="flex items-center justify-between"
                    >
                        <Typography variant="h6" color="blue-gray" className="font-semibold">
                            ALTA DE DESCUENTO EXTRAOFICIAL
                        </Typography>
                    </CardHeader>
                    <CardBody className="p-6">
                        <form className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nombre:</label>
                                <input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${editDisabled && 'bg-gray-50'}`}
                                    disabled={editDisabled}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nota:</label>
                                <textarea
                                    name="note"
                                    value={formData.note}
                                    onChange={handleChange}
                                    className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${editDisabled && 'bg-gray-50'}`}
                                    disabled={editDisabled}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Imagen de Autorización:
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className={`file:border-0 file:font-semibold file:py-2 file:px-4 file:rounded-full file:text-blue-500 file:bg-blue-50 w-full px-2 py-4 border border-gray-300 rounded-md ${editDisabled ? 'bg-gray-50' : 'cursor-pointer hover:bg-blue-gray-50  file:hover:bg-blue-100 hover:file:bg-blue-100 active:outline-none active:ring-2 active:ring-blue-500'}`}
                                    disabled={editDisabled}
                                />
                            </div>

                            {imagePreview && (
                                <div className="mt-4 relative">
                                    <p className="text-sm text-gray-600">Vista previa de la imagen:</p>
                                    <div
                                        className="mt-2 border border-gray-300 rounded-md overflow-hidden flex justify-center items-center relative">
                                        <img
                                            src={imagePreview}
                                            alt="Imagen de autorización"
                                            className="max-w-full max-h-60 object-contain"
                                        />
                                        {false &&
                                            <button
                                                onClick={() => {
                                                    setImagePreview(null);
                                                    setFormData({...formData, authorization_image: null});
                                                }}
                                                className="absolute top-0 left-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full mt-1"
                                            >
                                                <TrashIcon className="h-5 w-5"/>
                                            </button>
                                        }
                                    </div>
                                </div>
                            )}


                            <div>
                                <label className="block text-sm font-medium text-gray-700">Porcentaje de descuento
                                    (%):</label>
                                <input
                                    name="discount_percentage"
                                    type="number"
                                    value={formData.discount_percentage}
                                    onChange={handleChange}
                                    className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${editDisabled && 'bg-gray-50'}`}
                                    disabled={editDisabled}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Lista de Códigos de
                                    Predios:</label>
                                {formData.parcel_code_list.map((code, index) => (
                                    <div key={index} className="flex items-center space-x-2 mt-2">
                                        <InputMask
                                            mask="519999-999-999"
                                            value={code}
                                            onChange={(e) => handleParcelCodeChange(index, e.target.value)}
                                            disabled={editDisabled}
                                        >
                                            {(inputProps) => (
                                                <input
                                                    {...inputProps}
                                                    type="text"
                                                    placeholder="51XXXX-XXX-XXX"
                                                    className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${editDisabled && 'bg-gray-50'}`}
                                                />
                                            )}
                                        </InputMask>
                                        {!editDisabled &&
                                            <button
                                                type="button"
                                                onClick={() => removeParcelCode(index)}
                                                className="px-2 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                            >
                                                X
                                            </button>
                                        }
                                    </div>
                                ))}
                                {!editDisabled &&
                                    <button
                                        type="button"
                                        onClick={addParcelCode}
                                        className="mt-3 rounded border-2 px-2 border-blue-gray-200 text-blue-gray-200 hover:bg-blue-gray-50 w-full"
                                    >
                                        + Agregar Código de Predio
                                    </button>
                                }
                            </div>

                            <hr className="my-6"/>

                            <div
                                className={`flex items-center space-x-3 p-1 rounded ${currentRole !== "admin" ? "bg-gray-900/10" : ""}`}>
                                <input
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    type="checkbox"
                                    name="admin_approved"
                                    checked={formData.admin_approved}
                                    onChange={(e) => {
                                        if (currentRole === "admin") {
                                            setFormData({
                                                ...formData,
                                                admin_approved: e.target.checked,
                                            });
                                        }
                                    }}
                                    disabled={currentRole !== "admin" || discount?.applied}
                                />
                                <label className="text-sm font-medium text-gray-700 mx-2">Aprobado por
                                    Administrador</label>
                                {adminAproved &&
                                    <span className="text-sm text-gray-500">✔ {adminAproved}</span>}
                            </div>

                            <div
                                className={`flex items-center space-x-3 p-1 rounded ${currentRole !== "treasury" ? "bg-gray-900/10" : ""}`}>
                                <input
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    type="checkbox"
                                    name="treasury_approved"
                                    checked={formData.treasury_approved}
                                    onChange={(e) => {
                                        if (currentRole === "treasury") {
                                            setFormData({
                                                ...formData,
                                                treasury_approved: e.target.checked
                                            });
                                        }
                                    }}
                                    disabled={currentRole !== "treasury" || discount?.applied}
                                />
                                <label className="text-sm font-medium text-gray-700 mx-2">Aprobado por Tesorería</label>
                                {treasuryAproved &&
                                    <span className="text-sm text-gray-500">✔ {treasuryAproved}</span>}
                            </div>

                            {reqError && <p className="text-red-500 text-sm mt-2">{reqError}</p>}

                            {formErrors.length > 0 && (
                                <div className="p-3 mb-3 mt-6 bg-red-100 text-red-700 border border-red-400 rounded-md bg-red-500/20">
                                    <ul className="list-disc pl-5">
                                        {formErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <Button className="w-full hover:bg-blue-700 text-white py-2 rounded-md"
                                    onClick={handleSubmit} disabled={discount?.applied}>
                                {discount?.applied ? 'DESCUENTO APLICADO' : 'GUARDAR'}
                            </Button>
                        </form>
                    </CardBody>
                </Card>


                <Dialog open={showDialog} handler={() => setShowDialog(false)}>
                    <DialogHeader>
                        <Typography variant="h6" color="blue-gray">Confirmar</Typography>
                    </DialogHeader>
                    <DialogBody>
                        <Typography
                            variant="small"
                            className="text-[11px] font-medium uppercase text-blue-gray-400">
                            La aprobación por tesorería y administrador están seleccionadas. El descuento ({formData.discount_percentage}%)
                            quedará disponible para aplicarse al momento del pago de los predios de la lista ¿Deseas continuar?
                        </Typography>
                    </DialogBody>
                    <DialogFooter>
                        <Button variant="outlined" color="red" onClick={handleDialogCancel} className="mx-2" size="sm">
                            Cancelar
                        </Button>
                        <Button variant="gradient" color="green" onClick={handleDialogConfirm} size="sm">
                            Confirmar
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
        </>
    );
};

export default RegisterUnofficialDiscount;